/**
 * Crunch Design System — Menu, MenuItem, MenuSeparator, MenuSectionHeader
 *
 * Figma components:
 *   menu              (node 3442:4450)
 *   menu-item         (node 3442:4460)
 *   menu-separator    (node 3442:4478)
 *   menu-section-header (node 3942:7172)
 *
 * Usage:
 *   <Menu>
 *     <MenuSectionHeader label="Actions" />
 *     <MenuItem icon={<FontAwesomeIcon icon={faPencil} />}>Edit</MenuItem>
 *     <MenuItem icon={<FontAwesomeIcon icon={faCopy} />}>Duplicate</MenuItem>
 *     <MenuSeparator />
 *     <MenuItem icon={<FontAwesomeIcon icon={faTrash} />}>Delete</MenuItem>
 *   </Menu>
 */
import * as React from 'react';

export function Menu({
    children,
    className,
    ref,
    ...rest
}: {
    children?: React.ReactNode;
    className?: string;
    ref?: React.Ref<HTMLDivElement>;
    [key: string]: any;
}) {
    return (
        <div
            className={["cr-menu", className].filter(Boolean).join(" ")}
            ref={ref}
            role="menu"
            {...rest}
        >
            {children}
        </div>
    );
}

export function MenuItem({
    leading,
    icon,
    checked,
    active,
    disabled,
    onClick,
    name,
    value,
    children,
    className,
    ref,
    ...rest
}: {
    leading?: "checkbox" | "radio" | "icon";
    icon?: React.ReactNode;
    checked?: boolean;
    active?: boolean;
    disabled?: boolean;
    onClick?: () => void;
    name?: string;
    value?: string;
    children?: React.ReactNode;
    className?: string;
    ref?: React.Ref<HTMLElement>;
    [key: string]: any;
}) {
    const classes = [
        "cr-menu-item",
        active && "cr-menu-item--active",
        className,
    ]
        .filter(Boolean)
        .join(" ");

    const isToggle = leading === "checkbox" || leading === "radio";

    let leadingEl: React.ReactNode = null;
    if (leading === "checkbox") {
        leadingEl = (
            <span className="cr-menu-item__leading">
                <input
                    type="checkbox"
                    className="cr-checkbox"
                    defaultChecked={checked}
                    name={name}
                    value={value}
                    tabIndex={-1}
                />
            </span>
        );
    } else if (leading === "radio") {
        leadingEl = (
            <span className="cr-menu-item__leading">
                <input
                    type="radio"
                    className="cr-radio"
                    defaultChecked={checked}
                    name={name}
                    value={value}
                    tabIndex={-1}
                />
            </span>
        );
    } else if (leading === "icon" && icon) {
        leadingEl = <span className="cr-menu-item__leading">{icon}</span>;
    }

    // Checkbox/radio items render as <label> so clicking anywhere toggles the input.
    // Plain items render as <button>.
    const Tag = isToggle ? "label" : "button";

    return (
        <Tag
            className={classes}
            ref={ref as any}
            role={isToggle ? (leading === "checkbox" ? "menuitemcheckbox" : "menuitemradio") : "menuitem"}
            disabled={isToggle ? undefined : disabled}
            aria-disabled={disabled || undefined}
            onClick={onClick}
            {...rest}
        >
            {leadingEl}
            <span className="cr-menu-item__label">{children}</span>
        </Tag>
    );
}

export function MenuSeparator({
    className,
    ref,
    ...rest
}: {
    className?: string;
    ref?: React.Ref<HTMLDivElement>;
    [key: string]: any;
}) {
    return (
        <div
            className={["cr-menu-separator", className].filter(Boolean).join(" ")}
            ref={ref}
            role="separator"
            {...rest}
        />
    );
}

export function MenuSectionHeader({
    label = "Section header",
    className,
    ref,
    ...rest
}: {
    label?: string;
    className?: string;
    ref?: React.Ref<HTMLDivElement>;
    [key: string]: any;
}) {
    return (
        <div
            className={[
                "cr-menu-section-header",
                className,
            ]
                .filter(Boolean)
                .join(" ")}
            ref={ref}
            {...rest}
        >
            {label}
        </div>
    );
}
